import { useQuery, useMutation, useQueryClient } from '../useQuery'
import { queryKeys } from '@/api/queryKeys'
import * as procurementApi from '@/api/procurement'
import type {
  CreatePurchaseOrderRequest,
  ShipRequest,
  ReceiveRequest,
  PurchaseOrderPageParams,
} from '@/api/types/procurement'

export function usePurchaseOrders() {
  return useQuery({
    queryKey: queryKeys.purchaseOrders,
    queryFn: procurementApi.getPurchaseOrders,
  })
}

export function usePurchaseOrderPage(params: PurchaseOrderPageParams = {}) {
  return useQuery({
    queryKey: [...queryKeys.purchaseOrders, params],
    queryFn: () => procurementApi.getPurchaseOrderPage(params),
  })
}

export function usePurchaseOrder(id: number) {
  return useQuery({
    queryKey: queryKeys.purchaseOrder(id),
    queryFn: () => procurementApi.getPurchaseOrder(id),
    enabled: !!id,
  })
}

export function useCreatePurchaseOrder() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: CreatePurchaseOrderRequest) => procurementApi.createPurchaseOrder(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.purchaseOrders })
    },
  })
}

export function useApprovePurchaseOrder() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => procurementApi.approvePurchaseOrder(id),
    onSuccess: (data: { id: number }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.purchaseOrders })
      queryClient.invalidateQueries({ queryKey: queryKeys.purchaseOrder(data.id) })
    },
  })
}

export function usePlacePurchaseOrder() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => procurementApi.placePurchaseOrder(id),
    onSuccess: (data: { id: number }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.purchaseOrders })
      queryClient.invalidateQueries({ queryKey: queryKeys.purchaseOrder(data.id) })
    },
  })
}

export function useShipPurchaseOrder() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: ShipRequest }) =>
      procurementApi.shipPurchaseOrder(id, data),
    onSuccess: (data: { id: number }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.purchaseOrders })
      queryClient.invalidateQueries({ queryKey: queryKeys.purchaseOrder(data.id) })
    },
  })
}

export function useReceivePartialPurchaseOrder() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: ReceiveRequest }) =>
      procurementApi.receivePartialPurchaseOrder(id, data),
    onSuccess: (data: { id: number }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.purchaseOrders })
      queryClient.invalidateQueries({ queryKey: queryKeys.purchaseOrder(data.id) })
      queryClient.invalidateQueries({ queryKey: queryKeys.inventoryItems })
    },
  })
}

export function useReceiveAllPurchaseOrder() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: ReceiveRequest }) =>
      procurementApi.receiveAllPurchaseOrder(id, data),
    onSuccess: (data: { id: number }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.purchaseOrders })
      queryClient.invalidateQueries({ queryKey: queryKeys.purchaseOrder(data.id) })
      queryClient.invalidateQueries({ queryKey: queryKeys.inventoryItems })
    },
  })
}
